import Phaser from 'phaser';
import { ControlPanel } from './components/ControlPanel';
import { TopBar } from './components/TopBar';
import { BackgroundAnimation } from './components/BackgroundAnimation';
import { AudioManager } from './utilities/AudioManager';

const CARDS_TO_FLIP = 7;

export class SpinScene extends Phaser.Scene {
  private screenWidth = 750;
  private screenHeight = 1334;
  private selectedAmount;
  private selectedNumber;
  private controlPanel;
  private numberCardsTimeline: Phaser.Tweens.Timeline | undefined;
  private backgroundAnimation;

  private paused: boolean = false;

  private wheel;
  private wheelTween;
  private wheelSpinning = false;
  private wheelOptions = {
    // prize amounts, starting from 6 o'clock going anti-clockwise
    amounts: [20, 50, 100, 250, 5, 7, 10],

    // wheel rotation duration, in milliseconds
    rotationTime: 4500
  };

  constructor() {
    super('SpinScene');
  }

  preload() {
    const config = window._po_gm.config;
    this.wheelSpinning = false;
    this.selectedAmount = config?.winningPrize ? config?.winningPrize : '0';
    this.selectedNumber = config?.winningNumber ? parseInt(config?.winningNumber, 10) : 0;
  }

  create() {
    this.addBackground();
    new TopBar(this, this.screenWidth);
    this.addWheel();
    this.controlPanel = new ControlPanel(this, this.screenWidth, this.screenHeight);
    this.spinWheel();
    this.wheelSpinning = true;
  }

  addBackground() {
    const background = this.add.sprite(this.screenWidth / 2, this.screenHeight / 2, 'background');
    background.setDisplaySize(this.screenWidth, this.screenHeight);
    this.backgroundAnimation = new BackgroundAnimation(this, this.screenWidth, this.screenHeight);
    this.backgroundAnimation?.start();
  }

  addWheel() {
    this.wheel = this.add.image(this.screenWidth / 2, 174, 'wheel').setDepth(0);
    this.add.image(this.screenWidth / 2, 184, 'ticker').setDepth(0);
  }

  spinWheel() {
    if (!this.wheelSpinning) {
      this.wheelSpinning = true;
      AudioManager.getInstance().playSoundEffect(this, 'Casino_Spinning_Wheel');

      // the wheel will spin round from 2 to 4 times. This is just coreography
      const rounds = Phaser.Math.Between(2, 4);

      const prizeIndex = this.wheelOptions.amounts.indexOf(this.selectedAmount);

      // then will rotate by a random number from 0 to 360 degrees. This is the actual spin
      const degrees = prizeIndex * (360 / this.wheelOptions.amounts.length);

      // animation tween for the spin: duration 3s, will rotate by (360 * rounds + degrees) degrees
      // the quadratic easing will simulate friction
      this.wheelTween = this.tweens.add({
        targets: [this.wheel],
        angle: 360 * rounds + degrees - 10, // angle destination
        duration: this.wheelOptions.rotationTime,
        ease: 'Cubic.easeOut',
        callbackScope: this,
        completeDelay: 1000,
        onComplete: () => {
          this.addNumberCards();
        }
      });
    }
  }

  addNumberCards() {
    const positionX = this.screenWidth / 3.5,
      positionY = this.screenHeight / 1.35;

    const selectedNumbers: number[] = this.selectRandomNumbers();
    let winningCard;
    this.numberCardsTimeline = this.tweens.createTimeline();

    const fadeOutTargets: Phaser.GameObjects.Image[] = [];
    const fadeInTargets: Phaser.GameObjects.Image[] = [];
    const flipTargets: Phaser.GameObjects.Image[] = [];

    for (let i = 0; i < 15; i++) {
      const cardNumber = i + 1;
      const ballX = positionX - 100 + (i % 5) * 130,
        ballY = positionY - 120 + Math.floor(i / 5) * 100;

      const card = this.add.image(ballX, ballY, 'numberball_' + cardNumber).setScale(0);
      fadeInTargets.push(card);
      if (this.selectedNumber === cardNumber) {
        winningCard = card;
      } else {
        fadeOutTargets.push(card);
      }

      if (selectedNumbers.includes(cardNumber)) {
        flipTargets.push(card);
      }
    }

    this.shuffle(flipTargets);

    // add winning card to be the last to flip
    flipTargets.push(winningCard);
    // 1. fade in all cards
    this.numberCardsTimeline.add({
      targets: fadeInTargets,
      props: { scale: 0.38, alpha: 1 },
      ease: 'Linear',
      duration: 300,
      repeat: 0,
      yoyo: false
    });

    // 2. start flipping one by one
    flipTargets.forEach((card, index) => {
      const i = index + 1;
      this.addFlipAnimation(card, () => {
        AudioManager.getInstance().playSoundEffect(this, 'CardFlipSrc' + i);
      });
    });

    // 3. fade out all non winning cards
    this.numberCardsTimeline.add({
      targets: fadeOutTargets,
      props: { scale: 0, alpha: 0 },
      ease: 'Power2',
      duration: 200,
      repeat: 0,
      yoyo: false
    });

    // 4. show summary animations
    this.addSummaryAnimation(winningCard);
    this.numberCardsTimeline.play();
  }
  updateReplayButton() {
    this.controlPanel.button.setTexture('replay');
  }

  shuffle(cardsArray) {
    for (let i = cardsArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const temp = cardsArray[i];
      cardsArray[i] = cardsArray[j];
      cardsArray[j] = temp;
    }
  }

  pauseTimer() {
    if (!this.paused) {
      /**
       * Make sure sound effect always gets paused
       * Depending on audio API, an isPlaying condition might
       * not return correctly when the sound ends
       * The phaser library itself will also do a check if the sound is playing"
       * https://github.com/phaserjs/phaser/blob/v3.87.0/src/sound/BaseSound.js#L68
       */
      AudioManager.getInstance().pauseSoundEffect(this, 'Casino_Spinning_Wheel');
      this.wheelTween?.pause();
      this.numberCardsTimeline?.pause();
      this.backgroundAnimation?.pause();
      this.paused = true;
    }
  }

  resumeTimer() {
    if (this.paused) {
      AudioManager.getInstance().resumeSoundEffect(this);
      this.wheelTween?.resume();
      this.numberCardsTimeline?.resume();
      this.backgroundAnimation?.resume();
      this.paused = false;
    }
  }

  private selectRandomNumbers() {
    const numbers: number[] = [];
    while (numbers.length < CARDS_TO_FLIP) {
      const num = Math.floor(Math.random() * 15 + 1);
      if (num !== this.selectedNumber && !numbers.includes(num)) {
        numbers.push(num);
      }
    }
    return numbers;
  }

  private addFlipAnimation = (card: Phaser.GameObjects.Image, onComplete?: () => void) => {
    this.numberCardsTimeline?.add({
      targets: card,
      props: { scaleX: -0.5 },
      ease: 'Linear',
      duration: 380,
      repeat: 0,
      yoyo: true,
      onComplete
    });
  };

  private addSummaryAnimation = (winningCard: Phaser.GameObjects.Image) => {
    this.numberCardsTimeline?.add({
      targets: winningCard,
      x: this.screenWidth / 2,
      y: this.screenHeight * 0.755,
      ease: 'Linear',
      duration: 500
    });

    this.numberCardsTimeline?.add({
      targets: winningCard,
      props: { scale: 1.5 },
      ease: 'Linear',
      duration: 300
    });

    const logo = this.add
      .image(this.screenWidth / 2, this.screenHeight * 0.55, 'otwlogo')
      .setAlpha(0, 0, 0, 0)
      .setScale(0.6);

    this.numberCardsTimeline?.add({
      targets: logo,
      props: { alpha: 1 },
      ease: 'Linear',
      duration: 500,
      onComplete: () => {
        AudioManager.getInstance().playSoundEffect(this, 'Tile_Game_Connect');
        this.updateReplayButton();
      }
    });
  };
}
